import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckBadgeIcon, ClockIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/ApiService';
import type { AnalyticsEventName } from '../../services/api/analytics';
import type { GlobalSearchResult, GlobalSearchResults } from '../../types/buildExpansion';
import { useModalFocusTrap } from '../../hooks/useModalFocusTrap';

const RECENT_KEY = 'bilt.recentSearches';
const MAX_RECENT = 5;
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

/** Known group keys, in display order. API keys arrive lowercase. */
const GROUP_LABELS: { key: string; label: string }[] = [
  { key: 'vehicles', label: 'Vehicles' },
  { key: 'modifications', label: 'Modifications' },
  { key: 'brands', label: 'Brands' },
  { key: 'shops', label: 'Shops' },
  { key: 'members', label: 'Members' },
  { key: 'clubs', label: 'Clubs' },
  { key: 'events', label: 'Events' },
  { key: 'awards', label: 'Awards' },
  { key: 'achievements', label: 'Achievements' },
];

const prettifyKey = (key: string): string =>
  key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const track = (name: string, metadata?: Record<string, unknown>) => {
  void apiService.trackEvent(name as AnalyticsEventName, metadata ? { metadata } : {});
};

const loadRecentSearches = (): string[] => {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((t): t is string => typeof t === 'string').slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
};

const saveRecentSearch = (term: string): string[] => {
  const trimmed = term.trim();
  if (!trimmed) return loadRecentSearches();
  const next = [trimmed, ...loadRecentSearches().filter((t) => t.toLowerCase() !== trimmed.toLowerCase())].slice(
    0,
    MAX_RECENT
  );
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // localStorage unavailable (private mode etc.) — recents are best-effort
  }
  return next;
};

interface OrderedGroup {
  key: string;
  label: string;
  items: GlobalSearchResult[];
}

/** Returns present groups in canonical order, unknown keys last with prettified labels. */
const orderGroups = (results: GlobalSearchResults): OrderedGroup[] => {
  const groups: OrderedGroup[] = [];
  for (const { key, label } of GROUP_LABELS) {
    const items = results[key];
    if (Array.isArray(items) && items.length > 0) groups.push({ key, label, items });
  }
  const knownKeys = new Set(GROUP_LABELS.map((g) => g.key));
  for (const key of Object.keys(results)) {
    const items = results[key];
    if (!knownKeys.has(key) && Array.isArray(items) && items.length > 0) {
      groups.push({ key, label: prettifyKey(key), items });
    }
  }
  return groups;
};

type NavItem =
  | { kind: 'recent'; term: string }
  | { kind: 'result'; result: GlobalSearchResult; groupLabel: string }
  | { kind: 'view-all' };

interface GlobalSearchProps {
  className?: string;
  /** Icon-only trigger for compact header utility clusters. */
  variant?: 'button' | 'icon';
}

/**
 * Command-surface global search: trigger button + dialog. Full-screen sheet on
 * mobile, centered panel on desktop. Opens with "/" when focus is not in an input.
 */
export const GlobalSearch: React.FC<GlobalSearchProps> = ({ className = '', variant = 'button' }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recent, setRecent] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const requestSeq = useRef(0);

  useModalFocusTrap(open, panelRef);

  const trimmedQuery = query.trim();
  const groups = useMemo(() => (results ? orderGroups(results) : []), [results]);
  const totalResults = useMemo(() => groups.reduce((sum, g) => sum + g.items.length, 0), [groups]);
  const showRecent = trimmedQuery.length < MIN_QUERY_LENGTH;

  const navItems = useMemo<NavItem[]>(() => {
    if (showRecent) return recent.map((term) => ({ kind: 'recent', term }));
    const items: NavItem[] = [];
    for (const group of groups) {
      for (const result of group.items) items.push({ kind: 'result', result, groupLabel: group.label });
    }
    if (totalResults > 0) items.push({ kind: 'view-all' });
    return items;
  }, [showRecent, recent, groups, totalResults]);

  const openDialog = useCallback(() => {
    setRecent(loadRecentSearches());
    setOpen(true);
    track('global_search_opened');
  }, []);

  const closeDialog = useCallback(() => {
    setOpen(false);
    setQuery('');
    setResults(null);
    setError(null);
    setActiveIndex(-1);
    triggerRef.current?.focus();
  }, []);

  // "/" opens the dialog when the user is not typing somewhere else
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
      e.preventDefault();
      if (!open) openDialog();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, openDialog]);

  // Focus the input and lock body scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 50);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(timer);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return undefined;
    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      setResults(null);
      setLoading(false);
      setError(null);
      setActiveIndex(-1);
      return undefined;
    }
    setLoading(true);
    setError(null);
    const seq = ++requestSeq.current;
    const timer = window.setTimeout(async () => {
      try {
        const response = await apiService.globalSearch(trimmedQuery);
        if (seq !== requestSeq.current) return;
        setResults(response);
        setActiveIndex(-1);
        track('global_search_submitted', { query: trimmedQuery });
      } catch (e: unknown) {
        if (seq !== requestSeq.current) return;
        setResults(null);
        setError(e instanceof Error ? e.message : 'Search failed. Try again.');
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [open, trimmedQuery]);

  // Keep the active option in view
  useEffect(() => {
    if (activeIndex < 0) return;
    document.getElementById(`global-search-option-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const goToUrl = useCallback(
    (url: string) => {
      if (/^https?:\/\//i.test(url)) {
        window.location.assign(url);
      } else {
        navigate(url);
      }
    },
    [navigate]
  );

  const openAllResults = useCallback(() => {
    if (trimmedQuery.length < MIN_QUERY_LENGTH) return;
    setRecent(saveRecentSearch(trimmedQuery));
    setOpen(false);
    setQuery('');
    setResults(null);
    setActiveIndex(-1);
    navigate(`/search?q=${encodeURIComponent(trimmedQuery)}`);
  }, [navigate, trimmedQuery]);

  const openResult = useCallback(
    (result: GlobalSearchResult) => {
      setRecent(saveRecentSearch(trimmedQuery));
      track('global_search_result_opened', {
        query: trimmedQuery,
        type: result.type,
        id: result.id ?? null,
        url: result.url ?? null,
      });
      setOpen(false);
      setQuery('');
      setResults(null);
      setActiveIndex(-1);
      if (result.url) goToUrl(result.url);
    },
    [goToUrl, trimmedQuery]
  );

  const selectNavItem = useCallback(
    (item: NavItem) => {
      if (item.kind === 'recent') {
        setQuery(item.term);
        inputRef.current?.focus();
      } else if (item.kind === 'result') {
        openResult(item.result);
      } else {
        openAllResults();
      }
    },
    [openAllResults, openResult]
  );

  const clearRecent = () => {
    try {
      window.localStorage.removeItem(RECENT_KEY);
    } catch {
      // best-effort
    }
    setRecent([]);
    setActiveIndex(-1);
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (navItems.length > 0) setActiveIndex((i) => (i + 1) % navItems.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (navItems.length > 0) setActiveIndex((i) => (i <= 0 ? navItems.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < navItems.length) {
        selectNavItem(navItems[activeIndex]);
      } else {
        openAllResults();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeDialog();
    }
  };

  // Flat index across groups for aria-activedescendant option ids
  let optionIndex = -1;

  const triggerClass =
    variant === 'icon'
      ? `btn-icon min-h-[44px] min-w-[44px] ${className}`.trim()
      : `nav-link-inactive cursor-pointer min-h-[44px] ${className}`.trim();

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openDialog}
        className={triggerClass}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Search Bilt"
      >
        <MagnifyingGlassIcon className={variant === 'icon' ? 'w-6 h-6' : 'w-5 h-5'} aria-hidden />
        {variant === 'button' ? (
          <>
            <span>Search</span>
            <kbd
              className="hidden sm:inline-flex items-center justify-center w-5 h-5 rounded border border-white/[0.08] bg-[#0a0a0a] text-[11px] font-mono text-gray-500"
              aria-hidden
            >
              /
            </kbd>
          </>
        ) : null}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm animate-fade-in motion-reduce:animate-none sm:px-4 sm:pt-20"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDialog();
          }}
        >
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Search Bilt"
            className="flex flex-col bg-[#0c0c0c] h-full w-full sm:h-auto sm:max-h-[min(34rem,80vh)] sm:max-w-2xl sm:mx-auto sm:rounded-xl sm:border sm:border-white/[0.08] sm:shadow-elevated animate-slide-up motion-reduce:animate-none"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                closeDialog();
              }
            }}
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06] shrink-0">
              <MagnifyingGlassIcon className="w-5 h-5 text-gray-500 shrink-0" aria-hidden />
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-expanded={navItems.length > 0}
                aria-controls="global-search-listbox"
                aria-activedescendant={activeIndex >= 0 ? `global-search-option-${activeIndex}` : undefined}
                aria-autocomplete="list"
                aria-label="Search vehicles, shops, members, and more"
                placeholder="Search builds, shops, members…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(-1);
                }}
                onKeyDown={onInputKeyDown}
                className="flex-1 min-w-0 bg-transparent text-white placeholder-gray-500 text-base focus:outline-none min-h-[44px]"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
              {query.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    setActiveIndex(-1);
                    inputRef.current?.focus();
                  }}
                  className="btn-icon min-h-[44px] min-w-[44px]"
                  aria-label="Clear search"
                >
                  <XMarkIcon className="w-5 h-5" aria-hidden />
                </button>
              )}
              <button
                type="button"
                onClick={closeDialog}
                className="btn-icon min-h-[44px] min-w-[44px] sm:hidden"
                aria-label="Close search"
              >
                <XMarkIcon className="w-6 h-6" aria-hidden />
              </button>
              <kbd
                className="hidden sm:inline-flex items-center px-1.5 h-6 rounded border border-white/[0.08] bg-[#0a0a0a] text-[11px] font-mono text-gray-500"
                aria-hidden
              >
                Esc
              </kbd>
            </div>

            <div aria-live="polite" className="sr-only">
              {loading
                ? 'Searching…'
                : trimmedQuery.length >= MIN_QUERY_LENGTH && results
                ? totalResults === 1
                  ? '1 result'
                  : `${totalResults} results`
                : ''}
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain" id="global-search-listbox" role="listbox" aria-label="Search results">
              {showRecent ? (
                recent.length > 0 ? (
                  <div className="py-2">
                    <div className="flex items-center justify-between px-4 py-1.5">
                      <p className="eyebrow-muted">Recent searches</p>
                      <button
                        type="button"
                        onClick={clearRecent}
                        className="text-xs text-gray-400 hover:text-white underline underline-offset-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 px-2 py-2 min-h-[36px] cursor-pointer"
                      >
                        Clear
                      </button>
                    </div>
                    {recent.map((term, i) => {
                      optionIndex += 1;
                      const idx = optionIndex;
                      return (
                        <div
                          key={`${term}-${i}`}
                          id={`global-search-option-${idx}`}
                          role="option"
                          aria-selected={activeIndex === idx}
                          onClick={() => selectNavItem({ kind: 'recent', term })}
                          onMouseMove={() => setActiveIndex(idx)}
                          className={`flex items-center gap-3 px-4 min-h-[44px] cursor-pointer text-sm ${
                            activeIndex === idx ? 'bg-[#141414] text-white' : 'text-gray-300 hover:bg-white/[0.04]'
                          }`}
                        >
                          <ClockIcon className="w-4 h-4 text-gray-500 shrink-0" aria-hidden />
                          <span className="truncate">{term}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="px-4 py-8 text-sm text-gray-500 text-center">
                    Type at least {MIN_QUERY_LENGTH} characters to search builds, shops, members, clubs, and events.
                  </p>
                )
              ) : loading ? (
                <div className="flex items-center justify-center gap-3 py-10" role="status">
                  <span className="spinner-sm" aria-hidden />
                  <span className="text-sm text-gray-400">Searching…</span>
                </div>
              ) : error ? (
                <div className="px-4 py-6">
                  <div role="alert" className="alert-error">
                    {error}
                  </div>
                </div>
              ) : totalResults === 0 && results ? (
                <p className="px-4 py-10 text-sm text-gray-400 text-center" role="status">
                  No results for “{trimmedQuery}”. Try a different make, model, shop, or username.
                </p>
              ) : (
                groups.map((group) => (
                  <div key={group.key} className="py-2">
                    <p className="px-4 py-1.5 eyebrow-muted">{group.label}</p>
                    {group.items.map((result, i) => {
                      optionIndex += 1;
                      const idx = optionIndex;
                      return (
                        <div
                          key={`${group.key}-${result.id ?? i}`}
                          id={`global-search-option-${idx}`}
                          role="option"
                          aria-selected={activeIndex === idx}
                          onClick={() => openResult(result)}
                          onMouseMove={() => setActiveIndex(idx)}
                          className={`flex items-center gap-3 px-4 py-2 min-h-[44px] cursor-pointer ${
                            activeIndex === idx ? 'bg-[#141414]' : 'hover:bg-white/[0.04]'
                          }`}
                        >
                          {result.image_url ? (
                            <img
                              src={apiService.getFileUrl(result.image_url) || result.image_url}
                              alt=""
                              className="w-9 h-9 rounded-lg object-cover border border-white/[0.08] bg-[#0a0a0a] shrink-0"
                            />
                          ) : (
                            <div
                              className="w-9 h-9 rounded-lg bg-[#141414] border border-white/[0.08] flex items-center justify-center text-xs font-semibold text-gray-400 shrink-0"
                              aria-hidden
                            >
                              {(result.title || '?').charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-white truncate flex items-center gap-1.5">
                              <span className="truncate">{result.title}</span>
                              {result.verified ? (
                                <span className="inline-flex items-center gap-0.5 text-orange-300 shrink-0">
                                  <CheckBadgeIcon className="w-4 h-4" aria-hidden />
                                  <span className="sr-only">Verified</span>
                                </span>
                              ) : null}
                            </p>
                            {result.subtitle ? (
                              <p className="text-xs text-gray-500 truncate">{result.subtitle}</p>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}

              {!showRecent && !loading && !error && totalResults > 0 && (
                <div className="border-t border-white/[0.06]">
                  {(() => {
                    optionIndex += 1;
                    const idx = optionIndex;
                    return (
                      <div
                        id={`global-search-option-${idx}`}
                        role="option"
                        aria-selected={activeIndex === idx}
                        onClick={openAllResults}
                        onMouseMove={() => setActiveIndex(idx)}
                        className={`flex items-center justify-center gap-2 px-4 min-h-[48px] cursor-pointer text-sm font-medium ${
                          activeIndex === idx ? 'bg-[#141414] text-orange-300' : 'text-orange-300/90 hover:bg-white/[0.04]'
                        }`}
                      >
                        View all results for “{trimmedQuery}”
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="hidden sm:flex items-center gap-4 px-4 py-2.5 border-t border-white/[0.06] text-[11px] text-gray-500 shrink-0">
              <span>
                <kbd className="font-mono">↑↓</kbd> to navigate
              </span>
              <span>
                <kbd className="font-mono">Enter</kbd> to open
              </span>
              <span>
                <kbd className="font-mono">Esc</kbd> to close
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalSearch;
